import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
} from "chart.js";
import featureService from "../api/services/dashbaordService";
import Loading from "../components/Loading";
import userServices from "../api/services/userService";
import {
  DashboardUsersModal,
  DashboardOverviewCards,
  DashboardCharts,
  DashboardProposalsDetail,
  DashboardUsersTable,
} from "../components/Dashboard";

ChartJS.register(ArcElement, Tooltip, Legend);
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 10 }, (_, i) => currentYear - 5 + i);

function filterInternalUsers(users) {
  return (users || []).filter(
    (user) => !user.email || !user.email.toLowerCase().endsWith("@naseebimail.com")
  );
}

const Home = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showTodayUsersModal, setShowTodayUsersModal] = useState(false);
  const [showMonthUsersModal, setShowMonthUsersModal] = useState(false);
  const [todayUsers, setTodayUsers] = useState([]);
  const [totalToday, setTotalToday] = useState(0);
  const [monthUsers, setMonthUsers] = useState([]);
  const [totalMonth, setTotalMonth] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());
  const [currentPage, setCurrentPage] = useState(1);
  const [modalLoading, setModalLoading] = useState(false);
  const usersPerPage = 10;

  useEffect(() => {
    document.body.style.overflowX = "hidden";
    return () => {
      document.body.style.overflowX = "";
    };
  }, []);

  useEffect(() => {
    if (showTodayUsersModal || showMonthUsersModal) {
      document.body.style.overflow = "hidden";
      document.body.style.overflowX = "hidden";
    } else {
      document.body.style.overflow = "";
      document.body.style.overflowX = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [showTodayUsersModal, showMonthUsersModal]);

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      try {
        const result = await featureService.fetchAll();
        if (result?.responseCode === 200) {
          setDashboardData(result);
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const fetchTodayPage = async (page) => {
    setModalLoading(true);
    try {
      const result = await userServices.fetchAllUsers(page, null, "today");
      setTodayUsers(result?.users ?? []);
      setTotalToday(result?.total ?? 0);
      setCurrentPage(page);
    } catch (error) {
      console.error("Error fetching today users:", error);
    } finally {
      setModalLoading(false);
    }
  };

  const fetchMonthPage = async (page, month = selectedMonth, year = selectedYear) => {
    setModalLoading(true);
    try {
      const result = await userServices.fetchAllUsers(page, null, "month", month, year);
      setMonthUsers(result?.users ?? []);
      setTotalMonth(result?.total ?? 0);
      setCurrentPage(page);
    } catch (error) {
      console.error("Error fetching month users:", error);
    } finally {
      setModalLoading(false);
    }
  };

  const handleMonthYearChange = (month, year) => {
    setSelectedMonth(month);
    setSelectedYear(year);
    fetchMonthPage(1, month, year);
  };

  const handleTodayUsersClick = () => {
    setCurrentPage(1);
    setTodayUsers([]);
    setTotalToday(0);
    setShowTodayUsersModal(true);
    fetchTodayPage(1);
  };

  const handleMonthUsersClick = () => {
    const now = new Date();
    const m = now.getMonth() + 1;
    const y = now.getFullYear();
    setSelectedMonth(m);
    setSelectedYear(y);
    setCurrentPage(1);
    setMonthUsers([]);
    setTotalMonth(0);
    setShowMonthUsersModal(true);
    fetchMonthPage(1, m, y);
  };

  if (!dashboardData) {
    return (
      <div className="w-full flex justify-center items-center min-h-[calc(100vh-120px)] overflow-x-hidden">
        <Loading />
      </div>
    );
  }

  const {
    usersAnalytics,
    proposalsAnalytics,
    premium,
    latest,
    genderDistribution,
    weekGenderDistribution,
  } = dashboardData;

  const filteredPremium = filterInternalUsers(premium);
  const filteredLatest = filterInternalUsers(latest);

  return (
    <>
      <div className="rounded-xl w-full min-h-[calc(100vh-120px)] overflow-x-hidden">
        {loading ? (
          <div className="flex justify-center items-center min-h-[200px]">
            <Loading />
          </div>
        ) : (
          <div className="p-4 md:p-6 lg:p-6">
            <DashboardOverviewCards
              usersAnalytics={usersAnalytics}
              proposalsAnalytics={proposalsAnalytics}
              onTodayUsersClick={handleTodayUsersClick}
              onMonthUsersClick={handleMonthUsersClick}
            />

            <DashboardCharts
              genderDistribution={genderDistribution}
              weekGenderDistribution={weekGenderDistribution}
            />

            <DashboardProposalsDetail proposalsAnalytics={proposalsAnalytics} />

            <div className="w-full flex flex-col lg:flex-row gap-4 lg:gap-6 bg-white rounded-xl shadow-md border border-gray-100 p-4 lg:p-6">
              <div className="w-full lg:w-1/2">
                <DashboardUsersTable
                  title="Premium Users"
                  users={filteredPremium}
                  emptyMessage="No premium users found"
                  rowHoverClass="hover:bg-green-50"
                />
              </div>
              <div className="w-full lg:w-1/2">
                <DashboardUsersTable
                  title="Latest Users (Top 10)"
                  users={filteredLatest}
                  emptyMessage="No latest users found"
                  rowHoverClass="hover:bg-blue-50"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <DashboardUsersModal
        title="Today Users"
        isOpen={showTodayUsersModal}
        onClose={() => setShowTodayUsersModal(false)}
        users={todayUsers}
        usersPerPage={usersPerPage}
        currentPage={currentPage}
        onPageChange={(page) => fetchTodayPage(page)}
        totalCount={totalToday}
        loading={modalLoading}
      />
      <DashboardUsersModal
        title="Monthly Users"
        isOpen={showMonthUsersModal}
        onClose={() => setShowMonthUsersModal(false)}
        users={monthUsers}
        usersPerPage={usersPerPage}
        currentPage={currentPage}
        onPageChange={(page) => fetchMonthPage(page, selectedMonth, selectedYear)}
        totalCount={totalMonth}
        loading={modalLoading}
        headerExtra={
          <div className="flex flex-wrap items-center gap-3 font-main">
            <label className="text-sm font-medium text-gray-700">Month</label>
            <select
              value={selectedMonth}
              onChange={(e) => handleMonthYearChange(Number(e.target.value), selectedYear)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-800 focus:ring-2 focus:ring-red-500 focus:border-red-500"
              aria-label="Select month">
              {MONTHS.map((name, i) => (
                <option key={name} value={i + 1}>
                  {name}
                </option>
              ))}
            </select>
            <label className="text-sm font-medium text-gray-700">Year</label>
            <select
              value={selectedYear}
              onChange={(e) => handleMonthYearChange(selectedMonth, Number(e.target.value))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-800 focus:ring-2 focus:ring-red-500 focus:border-red-500"
              aria-label="Select year">
              {YEAR_OPTIONS.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        }
      />
    </>
  );
};

export default Home;
